import { useEffect, useMemo, useRef, useState } from 'react';
import type { Device, Release, UpdateState } from '@glyph/protocol-ts';
import { UPDATE_LOCK_ENABLED } from '@glyph/protocol-ts';
import {
  CURRENT_VERSION,
  fmtBytes,
  isNewer,
  useClient,
  useCommand,
  useDeviceId,
  useDevices,
  useLive,
  useLockUpdates,
  useReleases,
  useRollout,
  useSettings,
  useUpdateStatus, useCan, useApi, isLocalShell, effectiveUpdateState, } from '@glyph/core';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  EmptyState,
  ListRow,
  Note,
  PageHeader,
  ProgressBar,
  Section,
  SegmentedControl,
  StatusPill,
  SwitchRow,
  Tag,
  useToast,
} from '@glyph/ui';
import { fmtDateTs } from './shared';

export interface UpdatesScreenProps {
  /** Fleet stable version shown as FLEET STABLE (defaults to the newest stable release). */
  fleetStable?: string;
  /** Units ticked on the fleet page (group action Update); otherwise every unit behind the stable release. */
  initialSelection?: string[];
}
type Policy = 'skip_if_engine_running' | 'immediate';

const STATE_LABEL: Record<UpdateState, string> = {
  idle: 'IDLE',
  checking: 'CHECKING',
  available: 'AVAILABLE',
  downloading: 'DOWNLOADING',
  verifying: 'VERIFYING',
  extracting: 'EXTRACTING',
  staged: 'STAGED',
  installing: 'INSTALLING',
  switching: 'SWITCHING SLOT',
  restarting: 'RESTARTING',
  healthchecking: 'HEALTH CHECK',
  success: 'DONE',
  rollback: 'ROLLED BACK',
  failed: 'FAILED',
  skipped: 'SKIPPED · ENGINE RUNNING',
};
/** `update_locked` as reported by the device; a unit that never reported is treated as unlocked for selection (the device enforces). */
const isLocked = (d: Device) => UPDATE_LOCK_ENABLED && !!d.update_locked;
const stateLabel = (u: { state: UpdateState; error?: string | null }) =>
  u.state === 'skipped' && u.error === 'locked' ? 'SKIPPED · LOCKED' : STATE_LABEL[u.state];

/** Shown next to every "Updates locked" switch (cloud and local). */
export const UPDATE_LOCK_HELP =
  'While locked this unit refuses every update — cloud rollouts, "Install staged", "Install from file" and OS images — until an admin unlocks it. Roll back to the previous release and the automatic health-check rollback stay available. New units ship locked.';
const BUSY: UpdateState[] = [
  'checking',
  'downloading',
  'verifying',
  'extracting',
  'installing',
  'switching',
  'restarting',
  'healthchecking',
];

/** Updates (cloud): Stable/Beta releases, select release, per-device checkboxes, push to selected/all, Next idle/Now, progress, rules. */
export function UpdatesScreen({ fleetStable, initialSelection }: UpdatesScreenProps) {
  const [channel, setChannel] = useState<'stable' | 'beta'>('stable');
  // app = agent/renderer/UI bundle, no reboot; os = rootfs slot image, one reboot, automatic fallback
  const [kind, setKind] = useState<'app' | 'os'>('app');
  const [policy, setPolicy] = useState<Policy>('skip_if_engine_running');
  const [sel, setSel] = useState<string[]>([]);
  const [target, setTarget] = useState<string | null>(null);
  const [rolloutId, setRolloutId] = useState<string | undefined>();
  const [confirmNow, setConfirmNow] = useState<null | 'sel' | 'all'>(null);
  const releases = useReleases(channel, kind);
  const devices = useDevices();
  const rollout = useRollout(rolloutId);
  const lockUpdates = useLockUpdates();
  const toast = useToast();
  const list = releases.data ?? [];
  const installed = (d: Device) => (kind === 'os' ? d.os_version : d.agent_version);
  const stable =
    kind === 'os' ? (list.filter((r) => r.channel === 'stable').map((r) => r.version)[0] ?? '') : (fleetStable ?? list.filter((r) => r.channel === 'stable').map((r) => r.version)[0] ?? CURRENT_VERSION);
  const tgt = list.find((r) => r.version === target) ?? list.find((r) => r.version === stable) ?? list[0];
  const devs = useMemo(() => devices.data ?? [], [devices.data]);
  const unlocked = useMemo(() => devs.filter((d) => !isLocked(d)), [devs]);
  const lockedCount = devs.length - unlocked.length;
  const once = useRef(false);
  useEffect(() => {
    if (!once.current && devs.length) {
      once.current = true;
      const ids = new Set(initialSelection ?? []);
      setSel(ids.size ? devs.filter((d) => ids.has(d.id) && !isLocked(d)).map((d) => d.id) : devs.filter((d) => installed(d) !== stable && !isLocked(d)).map((d) => d.id));
    }
  }, [devs, stable, initialSelection]);
  // A device locked after it was ticked drops out of the selection.
  useEffect(() => {
    setSel((s) => s.filter((id) => !devs.some((d) => d.id === id && isLocked(d))));
  }, [devs]);

  const push = async (ids: string[]) => {
    ids = ids.filter((id) => !devs.some((d) => d.id === id && isLocked(d)));
    if (!tgt || !ids.length) return;
    const ro = await rollout.create({
      release_id: tgt.id,
      target: { kind: ids.length === devs.length ? 'all' : 'devices', ids },
      policy,
    });
    setRolloutId(ro.id);
    toast.push(`Rollout ${tgt.version} → ${ids.length} unit${ids.length === 1 ? '' : 's'}`, 'ok');
  };
  const startPush = (which: 'sel' | 'all') =>
    policy === 'immediate'
      ? setConfirmNow(which)
      : void push(which === 'sel' ? sel : unlocked.map((d) => d.id));
  const setLock = async (ids: string[] | 'all', locked: boolean) => {
    const r = await lockUpdates.lock(ids === 'all' ? { all: true, locked } : { device_ids: ids, locked });
    const queued = r.results.filter((x) => x.status === 'queued').length;
    toast.push(
      `${locked ? 'Locked' : 'Unlocked'} updates on ${r.results.length} unit${r.results.length === 1 ? '' : 's'}${queued ? ` · ${queued} queued until online` : ''}`,
      'ok',
    );
  };
  const ro = rollout.data;
  const roDone = !!ro && ro.status !== 'running' && ro.status !== 'pending';
  const roOk = ro ? ro.devices.filter((x) => x.update.state === 'success').length : 0;
  const roFailed = ro ? ro.devices.filter((x) => x.update.state === 'failed' || x.update.state === 'rollback').length : 0;
  const roWaiting = ro ? ro.devices.filter((x) => x.update.state === 'staged').length : 0;
  const pushTxt = ro
    ? roDone
      ? `${roOk} of ${ro.devices.length} on ${ro.version}${roFailed ? ` · ${roFailed} failed` : ''}${roWaiting ? ` · ${roWaiting} staged, waiting for idle` : ''}`
      : `Pushing ${ro.version} to ${ro.devices.length} unit${ro.devices.length === 1 ? '' : 's'} · ${ro.policy === 'immediate' ? 'installs and reboots as soon as it is staged' : 'installs once captions stop (re-checked every 5 min)'}`
    : '';
  const POLICY_HINT: Record<Policy, string> = {
    skip_if_engine_running: 'Download, verify and stage now; switch slots and restart only while captions are stopped and nothing is recording.',
    immediate: 'Download, verify, stage, then switch slots and restart at once. Live units drop captions for about 30 s.',
  };

  const relRow = (r: Release, mobile?: boolean) => (
    <button
      key={r.id}
      type="button"
      onClick={() => setTarget(r.version)}
      className={`flex w-full flex-wrap justify-between gap-4 border-0 border-t border-line px-4 py-3.5 text-left text-fg hover:bg-hover ${tgt?.id === r.id ? 'bg-selected' : 'bg-transparent'}`}
    >
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className={`font-mono font-medium leading-none ${mobile ? 'text-xl' : 'text-lg'}`}>
            {r.version}
          </span>
          <Tag tone={r.channel === 'beta' ? 'warn' : 'fg4'}>{r.channel}</Tag>
          {r.version === stable && <span className="meta text-ok">{kind === 'os' ? 'NEWEST OS' : 'FLEET STABLE'}</span>}
        </div>
        <span
          className={`text-pretty font-normal leading-[1.5] text-fg2 ${mobile ? 'text-md' : 'text-base'}`}
        >
          {r.notes}
        </span>
        {mobile && (
          <span className="font-mono text-sm text-fg4">
            {fmtDateTs(r.published_at, false)} · {fmtBytes(r.size_bytes)} · {r.signed ? 'signed' : 'UNSIGNED'}
          </span>
        )}
      </div>
      {!mobile && (
        <div className="flex flex-none flex-col items-end gap-1.5">
          <span className="font-mono text-sm text-fg3">{fmtDateTs(r.published_at, false)}</span>
          <span className="font-mono text-xs text-fg4">
            {fmtBytes(r.size_bytes)} · {r.signed ? 'signed · Ed25519' : 'UNSIGNED'}
          </span>
        </div>
      )}
    </button>
  );
  /** One line per unit: what will happen before a push, what is happening during one, what happened after. */
  const devState = (d: (typeof devs)[number]) => {
    if (isLocked(d)) return { t: 'LOCKED', tone: 'warn' as const };
    const rd = ro?.devices.find((x) => x.device_id === d.id);
    if (rd) {
      const u = rd.update;
      if (u.state === 'downloading') return { t: `DOWNLOADING${u.progress_pct != null ? ` ${Math.round(u.progress_pct)} %` : ''}`, tone: 'accent' as const };
      if (u.state === 'staged') return { t: ro?.policy === 'immediate' ? 'STAGED · INSTALLING' : 'STAGED · WAITING FOR IDLE', tone: 'accent' as const };
      if (u.state === 'healthchecking') return { t: 'HEALTH CHECK · 90 S', tone: 'accent' as const };
      if (u.state === 'success') return { t: `DONE · ON ${u.current_version || ro?.version || ''}`.trim(), tone: 'ok' as const };
      if (u.state === 'failed') return { t: `FAILED${u.last_error ? ` · ${u.last_error}` : ''}`, tone: 'bad' as const };
      if (u.state === 'rollback') return { t: 'ROLLED BACK', tone: 'bad' as const };
      if (u.state === 'skipped') return { t: stateLabel(u), tone: 'warn' as const };
      if (u.state === 'idle' && !d.online) return { t: 'OFFLINE · OFFER QUEUED', tone: 'warn' as const };
      return { t: stateLabel(u), tone: 'accent' as const };
    }
    if (!d.online) return { t: 'OFFLINE · WILL QUEUE', tone: 'warn' as const };
    if (!tgt) return { t: '—', tone: 'fg5' as const };
    const have = installed(d);
    const want = tgt.version.split('-')[0] ?? tgt.version;
    if (have === want) return { t: `ALREADY ON ${want}`, tone: 'fg5' as const };
    const newer = isNewer(want, have);
    return { t: newer ? `${have || '?'} → ${want}` : `DOWNGRADE TO ${want}`, tone: newer ? ('accent' as const) : ('warn' as const) };
  };
  const toggle = (id: string) => {
    if (devs.some((d) => d.id === id && isLocked(d))) return;
    setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };
  const whenSeg = (fw?: boolean) => (
    <SegmentedControl
      fullWidth={fw}
      value={policy}
      onChange={setPolicy}
      options={[
        { value: 'skip_if_engine_running', label: 'Next idle' },
        { value: 'immediate', label: 'Now' },
      ]}
    />
  );
  const progress = ro && (
    <div className="flex flex-col gap-2 border-t border-line px-4 py-3.5">
      <div className="flex items-center justify-between gap-2.5">
        <span className={`text-base ${roFailed ? 'text-warn' : roDone ? 'text-ok' : 'text-fg2'}`}>{pushTxt}</span>
        <span className="font-mono text-sm text-fg3 tabular">{Math.round(ro.progress_pct)} %</span>
      </div>
      <ProgressBar pct={ro.progress_pct} />
      {roDone && (
        <div>
          <Button size="xs" variant="ghost" mono onClick={() => setRolloutId(undefined)}>
            CLEAR
          </Button>
        </div>
      )}
    </div>
  );
  const policyHint = <span className="font-mono text-xs leading-[1.5] text-fg4">{POLICY_HINT[policy]}</span>;
  const lockCard = (mobile?: boolean) => (
    <Card>
      <CardHeader
        title="Updates locked"
        meta={`${lockedCount} OF ${devs.length} LOCKED`}
        metaTone={lockedCount ? 'warn' : undefined}
      />
      <div className="flex flex-wrap gap-2 px-4 pb-3 pt-2">
        <Button
          size="responsive"
          onClick={() => void setLock('all', true)}
          disabled={lockUpdates.isPending || !devs.length}
        >
          Lock updates on all
        </Button>
        <Button
          size="responsive"
          variant="primary"
          onClick={() => void setLock('all', false)}
          disabled={lockUpdates.isPending || !devs.length}
        >
          Unlock all
        </Button>
      </div>
      <div className="px-4">
        {devs.map((d) => (
          <SwitchRow
            key={d.id}
            title={d.name}
            hint={`${d.agent_version} · ${isLocked(d) ? 'LOCKED · refuses updates' : 'unlocked'}${d.online ? '' : ' · applies when online'}`}
            checked={isLocked(d)}
            disabled={lockUpdates.isPending}
            onChange={(v) => void setLock([d.id], v)}
          />
        ))}
      </div>
    </Card>
  );
  const rules =
    kind === 'os'
      ? 'An OS image is a whole root filesystem. The unit downloads and verifies it against the pinned release key, writes it to the inactive slot, then reboots once into it (at next idle, or now). If the new system does not pass its health check it boots back into the previous one by itself. Captions drop for about a minute during the reboot. The boot partition (kernel, first-stage scripts) is not changed by this; a new card image carries those.'
      : 'A unit that is LIVE never reboots for an update unless you choose Now: the release is downloaded, verified and staged, and applied only once the engine is stopped and not recording (re-checked every 5 min). Bundles are verified against the pinned release key (Ed25519/minisign) on the device before the slot is switched. A failed health check rolls back automatically and raises an alert.';
  const kindSeg = (fw?: boolean) => (
    <SegmentedControl
      fullWidth={fw}
      value={kind}
      onChange={(v) => {
        setKind(v);
        setTarget(null);
      }}
      options={[
        { value: 'app', label: 'App' },
        { value: 'os', label: 'OS image' },
      ]}
      aria-label="Release kind"
    />
  );

  return (
    <Section>
      <PageHeader
        desktopOnly
        title="Updates"
        meta="Signed A/B releases · installs to the inactive slot · automatic rollback if the new slot fails its health check"
        actions={
          <>
            {kindSeg()}
            <SegmentedControl
              value={channel}
              onChange={setChannel}
              options={[
                { value: 'stable', label: 'Stable' },
                { value: 'beta', label: 'Beta' },
              ]}
            />
          </>
        }
      />
      {/* mobile */}
      <div className="flex flex-col gap-4 md:hidden">
        {kindSeg(true)}
        <SegmentedControl
          fullWidth
          value={channel}
          onChange={setChannel}
          options={[
            { value: 'stable', label: 'Stable' },
            { value: 'beta', label: 'Beta' },
          ]}
        />
        <Card>
          <CardHeader title={kind === 'os' ? 'OS images' : 'Releases'} meta="SELECT TO TARGET" />
          {list.length === 0 && <EmptyState label={kind === 'os' ? 'No OS images on this channel' : 'No releases on this channel'} />}
          {list.map((r) => relRow(r, true))}
        </Card>
        <Card>
          <CardHeader title={`Roll out ${tgt?.version ?? ''}`} meta={`${sel.length} SELECTED · ${policy === 'immediate' ? 'NOW' : 'NEXT IDLE'}`} />
          {devs.map((d) => {
            const st = devState(d);
            return (
              <ListRow
                key={d.id}
                onPress={() => toggle(d.id)}
                trailing={<StatusPill tone={st.tone}>{st.t}</StatusPill>}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Checkbox
                    checked={sel.includes(d.id)}
                    disabled={isLocked(d)}
                    label={d.name}
                    onChange={() => toggle(d.id)}
                  />
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="text-lg text-fg">{d.name}</span>
                    <span className="font-mono text-sm text-fg4">
                      {kind === 'os' ? `os ${d.os_version || '?'}` : d.agent_version} · {d.state.toUpperCase()} · slot {d.slot}
                    </span>
                  </div>
                </div>
              </ListRow>
            );
          })}
          <div className="flex flex-col gap-2.5 border-t border-line px-4 pb-3.5 pt-3">
            <span className="text-lg text-fg">Apply</span>
            {whenSeg(true)}
            {policyHint}
          </div>
          <div className="flex gap-2 px-4 pb-4">
            <Button
              size="lg"
              variant="primary"
              onClick={() => startPush('sel')}
              disabled={!sel.length || rollout.isCreating}
            >
              Push to selected
            </Button>
            <Button
              size="lg"
              onClick={() => startPush('all')}
              disabled={rollout.isCreating || !unlocked.length}
            >
              Push to all{lockedCount ? ` unlocked (${unlocked.length})` : ''}
            </Button>
          </div>
          {progress}
        </Card>
        {UPDATE_LOCK_ENABLED && lockCard(true)}
        <Card mode="padded" gap="sm">
          <CardTitle>Rules</CardTitle>
          <span className="text-pretty text-md leading-[1.6] text-fg2">{rules}</span>
        </Card>
      </div>
      {/* desktop */}
      <div className="hidden flex-wrap items-start gap-5 md:flex">
        <Card className="min-w-0" style={{ flex: '1 1 480px' }}>
          <CardHeader title={kind === 'os' ? 'OS images' : 'Releases'} meta="SELECT TO TARGET" />
          {list.length === 0 && <EmptyState label={kind === 'os' ? 'No OS images on this channel' : 'No releases on this channel'} />}
          {list.map((r) => relRow(r))}
        </Card>
        <div className="flex min-w-[300px] flex-col gap-5" style={{ flex: '1 1 380px' }}>
          <Card>
            <CardHeader title={`Roll out ${tgt?.version ?? ''}`} meta={`${sel.length} SELECTED · ${policy === 'immediate' ? 'NOW' : 'NEXT IDLE'}`} />
            {devs.map((d) => {
              const st = devState(d);
              return (
                <div
                  key={d.id}
                  role="button"
                  tabIndex={0}
                  aria-disabled={isLocked(d)}
                  onClick={() => toggle(d.id)}
                  onKeyDown={(e) => e.key === 'Enter' && toggle(d.id)}
                  className={`flex items-center gap-3 border-t border-line px-4 py-[11px] ${isLocked(d) ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-hover'}`}
                >
                  <Checkbox
                    checked={sel.includes(d.id)}
                    disabled={isLocked(d)}
                    label={d.name}
                    onChange={() => toggle(d.id)}
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="text-md font-medium leading-none">{d.name}</span>
                    <span className="font-mono text-xs text-fg4">
                      {kind === 'os' ? `os ${d.os_version || '?'}` : d.agent_version} · {d.state.toUpperCase()} · slot {d.slot}
                    </span>
                  </div>
                  <span
                    className={`flex-none font-mono text-2xs font-medium tracking-meta ${st.tone === 'accent' ? 'text-accent' : st.tone === 'warn' ? 'text-warn' : st.tone === 'bad' ? 'text-bad' : 'text-fg5'}`}
                  >
                    {st.t}
                  </span>
                </div>
              );
            })}
            <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-3.5">
              <Button
                variant="primary"
                onClick={() => startPush('sel')}
                disabled={!sel.length || rollout.isCreating}
              >
                Push to selected
              </Button>
              <Button onClick={() => startPush('all')} disabled={rollout.isCreating || !unlocked.length}>
                Push to all{lockedCount ? ` unlocked (${unlocked.length})` : ''}
              </Button>
              <div className="ml-auto flex items-center gap-2">
                <span className="meta text-fg4">APPLY</span>
                {whenSeg()}
              </div>
            </div>
            <div className="px-4 pb-3">{policyHint}</div>
            {progress}
          </Card>
          {UPDATE_LOCK_ENABLED && lockCard()}
          <Card mode="padded" gap="sm">
            <CardTitle>Rules</CardTitle>
            <span className="text-pretty text-base leading-[1.6] text-fg2">{rules}</span>
          </Card>
        </div>
      </div>
      <Dialog
        open={!!confirmNow}
        onClose={() => setConfirmNow(null)}
        title={`Install ${tgt?.version ?? ''} now and reboot?`}
        meta="LIVE UNITS WILL DROP CAPTIONS FOR THE REBOOT"
        confirm={{
          label: 'Install now',
          destructive: true,
          onClick: async () => {
            await push(confirmNow === 'sel' ? sel : unlocked.map((d) => d.id));
            setConfirmNow(null);
          },
        }}
      >
        "Now" skips the idle check: the staged slot is switched and the unit reboots immediately. Choose "Next
        idle" to wait until the engine is stopped and not recording.
      </Dialog>
    </Section>
  );
}

/** Local shell: single-device update panel — check, install staged, install from file, roll back, downgrade. */
export function DeviceUpdatePanel({ deviceId }: { deviceId?: string }) {
  const live = useLive(deviceId);
  const status = useUpdateStatus(deviceId);
  const settings = useSettings(deviceId);
  const client = useClient();
  const resolvedId = useDeviceId(deviceId);
  const refetchSettings = settings.refetch;
  // A lock flipped from the other side (cloud fan-out, another operator) shows up at once.
  useEffect(
    () => client.subscribe(resolvedId, ['settings_changed'], () => void refetchSettings()),
    [client, resolvedId, refetchSettings],
  );
  const cmd = useCommand(deviceId);
  const api = useApi();
  const toast = useToast();
  const canApply = useCan('update.apply'); // admin+ on the cloud (operators and viewers only watch); always on the unit's own shell
  const [confirm, setConfirm] = useState<null | 'rollback' | 'downgrade'>(null);
  const rawU = live.update ?? status.data;
  // a stale "failed" for the version the unit already runs is shown as done (see effectiveUpdateState)
  const u = rawU ? { ...rawU, state: (effectiveUpdateState(rawU) ?? rawU.state) as UpdateState, last_error: effectiveUpdateState(rawU) === 'success' ? null : rawU.last_error } : rawU;
  const busy = !!u && BUSY.includes(u.state);
  // Only offer what would do something. A unit keeps reporting the version it last staged after installing it, so
  // "staged" alone is not a reason to show Install: the staged release must be newer than what runs (or older, for a
  // downgrade). Roll back needs a different previous release.
  const current = u?.current_version || live.snapshot?.agent_version || '';
  const staged = u?.staged_version || '';
  const stagedNewer = !!staged && isNewer(staged, current);
  const stagedOlder = !!staged && !!current && isNewer(current, staged);
  const canRollBack = !!u?.rollback_available && !!u.previous_version && u.previous_version !== current;
  // The lock is off fleet-wide: never treated as locked here, and a unit still reporting lock=true is unlocked once.
  const reportedLock = settings.data?.update?.lock ?? false;
  const locked = UPDATE_LOCK_ENABLED && reportedLock;
  const unlockSent = useRef(false);
  useEffect(() => {
    if (UPDATE_LOCK_ENABLED || !reportedLock || unlockSent.current) return;
    unlockSent.current = true;
    void cmd.run('settings.patch', { patch: { update: { lock: false } } }).then(() => settings.refetch());
  }, [reportedLock, cmd, settings]);
  const setLock = async (v: boolean) => {
    const r = await cmd.run('settings.patch', { patch: { update: { lock: v } } });
    if (r) {
      toast.push(v ? 'Updates locked' : 'Updates unlocked', v ? 'warn' : 'ok');
      await settings.refetch();
    } else if (cmd.lastError) toast.push(cmd.lastError.message, 'bad');
  };
  const run = async (
    a: 'update.check' | 'update.apply' | 'update.rollback' | 'update.cancel',
    force = false,
  ) => {
    // Check for updates in the cloud console is answered by the cloud itself, so it works for every unit (agent
    // 0.1.10 cannot ask the cloud and only ever said "up to date"); the unit's own page still asks the unit.
    if (a === 'update.check' && !isLocalShell(client)) {
      try {
        const c = await api.updateCheck(resolvedId);
        toast.push(c.available ? (c.offered ? `${c.newest} available · downloading, then Install` : c.message) : `Up to date · newest ${c.channel} is ${c.newest ?? 'none'}`, c.available && !c.offered ? 'warn' : 'ok');
        void status.refetch();
      } catch (e) {
        toast.push((e as Error).message || 'Could not check for updates', 'bad');
      }
      return;
    }
    const r = await cmd.run(a, a === 'update.apply' ? { force } : {});
    if (r) {
      if (a === 'update.check') {
        const c = r as { newest?: string | null; available?: boolean; offered?: boolean; message?: string; channel?: string };
        toast.push(
          c.available ? (c.offered ? `${c.newest} available · downloading, then Install` : `${c.newest} available · ${c.message || 'offer not sent'}`) : `Up to date · newest ${c.channel ?? 'stable'} is ${c.newest ?? 'none'}`,
          c.available && !c.offered ? 'warn' : 'ok',
        );
      } else {
        const st =
          (r as { status?: { state?: UpdateState } }).status?.state ?? (r as { state?: UpdateState }).state;
        toast.push(`${a.split('.')[1]} · ${st ?? 'ok'}`, st === 'failed' ? 'bad' : 'ok');
      }
      void status.refetch();
    } else if (cmd.lastError) toast.push(cmd.lastError.message, 'bad');
  };
  return (
    <Card>
      <CardHeader
        title="Software"
        meta={`${u?.current_version || live.snapshot?.agent_version || '—'}${u?.previous_version ? ` · PREV ${u.previous_version}` : ''}${locked ? ' · LOCKED' : ''}`}
        metaTone={locked ? 'warn' : undefined}
      />
      {UPDATE_LOCK_ENABLED && (
        <div className={`px-4 ${locked ? 'bg-selected' : ''}`}>
          <SwitchRow
            first
            title="Updates locked"
            hint={locked ? 'refuses every update until unlocked' : 'cloud and local updates allowed'}
            checked={locked}
            disabled={settings.isLoading || !!cmd.pending['settings.patch']}
            onChange={(v) => void setLock(v)}
            className="md:border-t-0"
          />
        </div>
      )}
      <ListRow
        label="STATE"
        title={u ? stateLabel(u) : '—'}
        sub={
          u?.last_error ??
          (stagedNewer
            ? `staged ${staged} · press Install ${staged}`
            : stagedOlder
              ? `staged ${staged} is older than ${current} · Downgrade installs it`
              : u?.target_version && isNewer(u.target_version, current)
                ? `target ${u.target_version}`
                : 'up to date · nothing staged to install')
        }
        trailing={
          u?.progress_pct != null && u.progress_pct < 100 ? (
            <span className="meta text-accent">{Math.round(u.progress_pct)} %</span>
          ) : undefined
        }
      />
      {u?.progress_pct != null && u.progress_pct < 100 && (
        <div className="px-4 pb-3">
          <ProgressBar pct={u.progress_pct} />
        </div>
      )}
      {!canApply && <Note className="block border-t border-line px-4 py-3">Updates are installed by admins and owners; this account can only watch.</Note>}
      {canApply && (
      <div className="flex flex-wrap gap-2 border-t border-line px-4 py-3.5">
        <Button
          size="responsive"
          onClick={() => void run('update.check')}
          disabled={busy || !!cmd.pending['update.check']}
        >
          Check for updates
        </Button>
        {stagedNewer && (
          <Button size="responsive" variant="primary" disabled={busy || locked} onClick={() => void run('update.apply')}>
            Install {staged}
          </Button>
        )}
        {canRollBack && (
          <Button size="responsive" onClick={() => setConfirm('rollback')} disabled={busy}>
            Roll back to {u!.previous_version}
          </Button>
        )}
        {stagedOlder && (
          <Button size="responsive" variant="destructive-outline" onClick={() => setConfirm('downgrade')} disabled={busy || locked}>
            Downgrade to {staged}
          </Button>
        )}
        {busy && (
          <Button size="responsive" variant="ghost" mono onClick={() => void run('update.cancel')}>
            CANCEL
          </Button>
        )}
      </div>
      )}
      <Dialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm === 'rollback' ? 'Roll back to the previous slot?' : 'Downgrade this unit?'}
        meta={
          confirm === 'rollback'
            ? `RESTORES ${u?.previous_version ?? 'THE INACTIVE SLOT'} · ONE REBOOT`
            : `INSTALLS THE OLDER ${staged} OVER ${current}`
        }
        confirm={{
          label: confirm === 'rollback' ? 'Roll back' : 'Downgrade',
          destructive: true,
          onClick: async () => {
            await run(confirm === 'rollback' ? 'update.rollback' : 'update.apply', true);
            setConfirm(null);
          },
        }}
      >
        {confirm === 'rollback'
          ? 'Captions drop for the reboot. Settings and recordings on /data are untouched.'
          : 'The unit restarts on the older release. Captions drop for the restart.'}
      </Dialog>
    </Card>
  );
}
